import { AnchorHTMLAttributes, FC, ForwardRefExoticComponent, useCallback } from 'react';

import { Pagination as PaginationPurpur } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { withParams } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { SEARCH_RESULTS_PER_PAGE_DEFAULT_SIZE, SEARCH_RESULTS_PER_PAGE_SIZES } from '../../constants';
import { SEARCH_TEST_ID } from '../../test/test-ids';
import { DefaultPreventedLinkElement } from './LinkElement';
import { PageChangeHandler } from './model';
import { PaginationCount, PaginationWrapper } from './Pagination.styles';

type LinkElement = ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>>;
type Props = {
    offset: number;
    pageNumber: number;
    pageLimit: number;
    totalCount: number;
    getPageLink: (page: number) => string;
    onPageChange?: PageChangeHandler;
    showPaginationCount?: boolean;
    showItemsPerPageSelector?: boolean;
};

export const Pagination: FC<Props> = ({
    offset,
    pageNumber,
    pageLimit = SEARCH_RESULTS_PER_PAGE_DEFAULT_SIZE,
    totalCount,
    getPageLink,
    onPageChange,
    showPaginationCount = true,
    showItemsPerPageSelector,
}) => {
    const { t } = useTranslations();
    const UID = useComponentId('pagination');

    const pageChangeHandler = useCallback(
        ({ currentPage, pageSize }) => onPageChange?.(pageSize, currentPage),
        /* ignore new reference functions passed by parent */
        [],
    );
    const hrefGetter = useCallback(
        ({ page }) => getPageLink(page),
        /* ignore new reference functions passed by parent */
        [],
    );

    const itemsPerPageSelectorProps = {
        availablePageSizes: SEARCH_RESULTS_PER_PAGE_SIZES,
        pageSizeLabel: t['search.component.pagination.items_per_page_label'],
    };

    return (
        <PaginationWrapper>
            {showPaginationCount && (
                <PaginationCount data-test={SEARCH_TEST_ID.PAGINATION_COUNT}>
                    {withParams(t['search.component.pagination.count'], {
                        pageStart: offset + 1,
                        pageEnd: Math.min(offset + pageLimit, totalCount),
                        total: totalCount,
                    })}
                </PaginationCount>
            )}

            <PaginationPurpur
                {...(showItemsPerPageSelector && itemsPerPageSelectorProps)}
                currentPage={pageNumber}
                asLink={true}
                pageSize={pageLimit}
                linkElement={
                    DefaultPreventedLinkElement as LinkElement /* from React v19 ref can be passed as regular prop */
                }
                hrefGetter={hrefGetter}
                navigationFunction={pageChangeHandler}
                onPageChange={pageChangeHandler}
                outOfLabel={t['search.component.pagination.out_of_label']}
                pageSelectorId={`${UID}-mobile-page-selector`}
                pageSelectorLabel={t['search.component.pagination.mobile_page_selector_label']}
                nextButtonAriaLabel={t['search.component.pagination.next_page']}
                nextButtonText={t['search.component.pagination.next_page']}
                previousButtonAriaLabel={t['search.component.pagination.previous_page']}
                previousButtonText={t['search.component.pagination.previous_page']}
                stepNumberPrefix={t['search.component.pagination.step_number_prefix']}
                totalItems={totalCount}
                data-test={SEARCH_TEST_ID.PAGINATION_PAGES}
            />
        </PaginationWrapper>
    );
};
